import * as v from 'valibot';
import type { StructuredContent, StructuredContentDocument } from './types';
import { isStructuredContent } from './utils';

const marketplaceFieldPropsSchema = v.object({
  id: v.string(),
  field: v.string(),
  marketplaceField: v.object({ value: v.string(), isActive: v.boolean() }),
});

export const getAllChildrenStrings = (
  sc: StructuredContentDocument<Record<string, unknown>>,
  prevValue = '',
): string => {
  if (typeof sc.props?.children === 'string') {
    return prevValue.concat(sc.props.children);
  }
  if (Array.isArray(sc.props?.children)) {
    for (const child of sc.props.children) {
      if (typeof child === 'string') {
        prevValue = prevValue.concat(child);
      }
      if (isStructuredContent(child)) {
        const isMarketplaceFieldSC = v.safeParse(marketplaceFieldPropsSchema, child.props);
        if (isMarketplaceFieldSC.success) {
          const { marketplaceField } = isMarketplaceFieldSC.output;
          prevValue = prevValue.concat(marketplaceField.value);
        } else {
          prevValue = getAllChildrenStrings(child, prevValue);
        }
      }
    }
  }
  if (isStructuredContent(sc?.props?.children)) {
    prevValue = getAllChildrenStrings(sc?.props?.children, prevValue);
  }
  return prevValue;
};

export const getHeadingId = (childrenString: string) =>
  childrenString
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();

export const getTableOfContentsTargetText = ({
  overrideText,
  children,
}: {
  overrideText?: unknown;
  children?: StructuredContent | StructuredContent[];
}): string =>
  typeof overrideText === 'string' && overrideText.trim()
    ? overrideText
    : getAllChildrenStrings({ type: '', version: '', props: { children } });

export type TableOfContentsEntryType = 'H2' | 'H3' | 'TableOfContentsTarget';
export type TableOfContentsEntry = {
  target: string;
  textContent: string;
  type: TableOfContentsEntryType;
};

// Page render order; faqs/sidebarLinks/stickyBottom intentionally excluded.
// Flex roundups reorder mainContent/fullWidthContent/productCardsSection via contentSectionOrder (see pageOrder).
export const PAGE_SECTIONS = [
  'content',
  'contentAbove',
  'fullWidthInfo',
  'main',
  'contentBelow',
  'aboveSections',
  'mainContent',
  'fullWidthContent',
  'productCardsSection',
  'belowSections',
  'methodology',
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;

// Rebuilds a section, passing every structured content node (children first) through visit.
export const mapStructuredContent = (
  value: unknown,
  visit: (node: StructuredContentDocument) => StructuredContentDocument,
): unknown => {
  if (Array.isArray(value)) return value.map(v => mapStructuredContent(v, visit));
  if (isStructuredContent(value)) {
    const props: Record<string, unknown> = { ...value.props };
    if (props.children !== undefined) props.children = mapStructuredContent(props.children, visit);
    if (Array.isArray(props.tabs)) props.tabs = mapStructuredContent(props.tabs, visit);
    return visit({ ...value, props });
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, mapStructuredContent(v, visit)]),
    );
  }
  return value;
};

const childNodes = (node: StructuredContentDocument): unknown[] => [
  ...[node.props.children ?? []].flat(),
  ...(Array.isArray(node.props.tabs) ? node.props.tabs : []),
];

const toEntry = (node: StructuredContentDocument): TableOfContentsEntry | undefined => {
  if (node.props.excludeFromTOC) return;
  const level = node.props.level;
  const type: TableOfContentsEntryType | undefined =
    node.type === 'TableOfContentsTarget'
      ? 'TableOfContentsTarget'
      : node.type === 'ContentHeading' && level === 2
        ? 'H2'
        : node.type === 'ContentHeading' && level === 3
          ? 'H3'
          : undefined;
  if (!type) return;
  const textContent =
    type === 'TableOfContentsTarget'
      ? getTableOfContentsTargetText(node.props)
      : getAllChildrenStrings(node);
  const target = getHeadingId(textContent);
  return target ? { target, textContent, type } : undefined;
};

// Flex roundup CMS keys → hydrated section keys (financial-advisors-flex-template-roundup render order).
const FLEX_SECTION_KEYS: Record<string, string> = {
  mainContent: 'mainContent',
  fullWidthContent: 'fullWidthContent',
  roundupSections: 'productCardsSection',
};

// PAGE_SECTIONS with the flex slots refilled in contentSectionOrder order.
const pageOrder = (data: Record<string, unknown>): string[] => {
  const order = Array.isArray(data.contentSectionOrder)
    ? data.contentSectionOrder.map(key => FLEX_SECTION_KEYS[key as string]).filter(Boolean)
    : [];
  let i = 0;
  return PAGE_SECTIONS.map(key => (order.includes(key) ? order[i++] : key));
};

export function populatePageTableOfContents<T>(data: T, sections: readonly PropertyKey[]): T {
  if (!isPlainObject(data)) return data;

  const entries: TableOfContentsEntry[] = [];
  const collect = (value: unknown): void => {
    if (Array.isArray(value)) return value.forEach(collect);
    if (isStructuredContent(value)) {
      // ponytail: variant is picked client-side, so no Experiment heading is linkable server-side.
      if (value.type === 'Experiment') return;
      const entry = toEntry(value);
      if (entry && !entries.some(e => e.type === entry.type && e.target === entry.target)) {
        entries.push(entry);
      }
      return childNodes(value).forEach(collect);
    }
    if (isPlainObject(value)) Object.values(value).forEach(collect);
  };
  pageOrder(data)
    .filter(key => sections.includes(key))
    .forEach(key => collect(data[key]));
  if (!entries.length) return data;

  const fill = (value: unknown) =>
    mapStructuredContent(value, node => {
      if (node.type !== 'TableOfContents' || node.props.autoPopulateItems === false) return node;
      const filter = (node.props.entryFilter as TableOfContentsEntryType | undefined) ?? 'H2';
      const items = entries.filter(e => e.type === filter);
      return items.length ? { ...node, props: { ...node.props, items } } : node;
    });

  const result: Record<string, unknown> = { ...data };
  for (const key of sections) {
    if (typeof key === 'string' && key in result) result[key] = fill(result[key]);
  }
  return result as unknown as T;
}
